import React, { useEffect, useState } from 'react';
import { getSessionTypes } from '../../api/athlete';
import { SessionType } from '../../types/athlete';
import { Search, Droplets, Bike, PersonStanding, Dumbbell } from 'lucide-react';

const SessionTypeList: React.FC = () => {
  const [sessionTypes, setSessionTypes] = useState<SessionType[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [disciplineFilter, setDisciplineFilter] = useState<string>('all');

  useEffect(() => {
    loadSessionTypes();
  }, []);

  const loadSessionTypes = async () => {
    try {
      const data = await getSessionTypes();
      setSessionTypes(data);
    } catch (error) {
      console.error('Error loading session types:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDisciplineIcon = (discipline: string) => {
    switch (discipline) {
      case 'swim': return <Droplets className="text-blue-400" size={20} />;
      case 'bike': return <Bike className="text-green-400" size={20} />;
      case 'run': return <PersonStanding className="text-orange-400" size={20} />;
      case 'strength': return <Dumbbell className="text-purple-400" size={20} />;
      default: return <Dumbbell size={20} />;
    }
  };

  const getDisciplineColor = (discipline: string) => {
    switch (discipline) {
      case 'swim': return 'bg-blue-500/10 border-blue-500/20 text-blue-400';
      case 'bike': return 'bg-green-500/10 border-green-500/20 text-green-400';
      case 'run': return 'bg-orange-500/10 border-orange-500/20 text-orange-400';
      case 'strength': return 'bg-purple-500/10 border-purple-500/20 text-purple-400';
      default: return 'bg-gray-500/10 border-gray-500/20 text-gray-400';
    }
  };

  const filteredTypes = sessionTypes.filter(type => {
    const matchesSearch = type.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          type.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDiscipline = disciplineFilter === 'all' || type.discipline === disciplineFilter;
    return matchesSearch && matchesDiscipline;
  });

  const groupedByDiscipline = filteredTypes.reduce((acc, type) => {
    if (!acc[type.discipline]) {
      acc[type.discipline] = [];
    }
    acc[type.discipline].push(type);
    return acc;
  }, {} as Record<string, SessionType[]>);

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading session types...</div>;
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Session Types</h1>
        <p className="text-muted">Predefined training session types for your plans</p>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-8">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#718096]" size={18} />
          <input
            type="text"
            placeholder="Search session types..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10"
          />
        </div>
        <div className="flex gap-2">
          {['all', 'swim', 'bike', 'run', 'strength'].map((disc) => (
            <button
              key={disc}
              onClick={() => setDisciplineFilter(disc)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                disciplineFilter === disc
                  ? 'bg-[#3b82f6] text-white'
                  : 'bg-[#141921] text-[#e2e8f0] hover:bg-[#2d3748]'
              }`}
            >
              {disc.charAt(0).toUpperCase() + disc.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Results */}
      {Object.entries(groupedByDiscipline).map(([discipline, types]) => (
        <div key={discipline} className="mb-8">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            {getDisciplineIcon(discipline)}
            {discipline.charAt(0).toUpperCase() + discipline.slice(1)} Sessions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {types.map((type) => (
              <div key={type.id} className="card-base p-5">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-semibold text-lg">{type.name}</h3>
                  <span className={`px-2 py-1 rounded text-xs border ${getDisciplineColor(type.discipline)}`}>
                    {type.session_category}
                  </span>
                </div>
                <p className="text-muted text-sm mb-3">{type.description}</p>
                <div className="flex items-center gap-4 text-sm text-[#718096]">
                  {type.default_duration_minutes && (
                    <span>⏱ {type.default_duration_minutes} min</span>
                  )}
                  {type.default_pace_type && (
                    <span>🏃 {type.default_pace_type}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {filteredTypes.length === 0 && (
        <div className="card-base p-8 text-center text-muted">
          <p>No session types found matching your criteria</p>
        </div>
      )}
    </div>
  );
};

export default SessionTypeList;
